
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Card } from "../ui/Card";
import { Shield, FileText, Scale, Lock, Gavel, FileCheck, CheckCircle, ChevronRight } from "lucide-react";

const AuditLineInternal = ({ title, status }: { title: string, status: string }) => (
    <div className="flex items-center justify-between py-3 border-b border-stone-100 dark:border-white/5 last:border-0 gap-4">
        <span className="text-stone-400 font-bold text-[9px] uppercase tracking-widest">{title}</span>
        <div className="flex items-center gap-2 shrink-0">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="text-emerald-500 font-mono text-[10px] font-bold">{status}</span>
        </div>
    </div>
);

export const LegalView: React.FC = () => {
  return (
    <div className="space-y-8 max-w-6xl mx-auto animate-fade-in pb-12 px-4 sm:px-0">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-stone-200 dark:border-white/10 pb-8">
         <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-stone-900 dark:bg-white flex items-center justify-center text-nobel-gold dark:text-[#0A0B0F] shadow-xl shrink-0">
               <Scale size={28} />
            </div>
            <div>
               <h2 className="font-serif text-2xl md:text-4xl text-[#0A0B0F] dark:text-white font-bold tracking-tight">Legal Repository</h2>
               <p className="text-stone-500 dark:text-stone-400 mt-1 text-[10px] sm:text-sm font-medium uppercase tracking-widest">Sovereign Compliance Framework</p>
            </div>
         </div>
         <div className="text-left md:text-right">
            <div className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.3em]">Latest Revision</div>
            <div className="text-xs sm:text-sm font-mono text-[#0A0B0F] dark:text-white font-bold uppercase tracking-widest">Verified Status</div>
         </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
              <Card noPadding className="overflow-hidden border-stone-200 dark:border-white/10 shadow-sm">
                  <div className="p-6 border-b border-stone-100 dark:border-white/5 bg-stone-50/50 dark:bg-white/[0.02] flex items-center gap-3">
                      <FileText className="text-nobel-gold shrink-0" size={20}/>
                      <h3 className="font-serif font-bold text-lg md:text-xl text-[#0A0B0F] dark:text-white">Master Service Agreement</h3>
                  </div>
                  <div className="p-6 md:p-8 prose dark:prose-invert max-w-none text-xs md:text-sm text-stone-600 dark:text-stone-400 leading-relaxed space-y-6">
                      <p>
                          GhostPay operates as a non-custodial gateway and secure runtime (GhostOS). By utilizing these services, you enter into a binding agreement governed by the protocols of the Sovereign Digital Jurisdiction.
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div>
                            <h4 className="font-serif font-bold text-[#0A0B0F] dark:text-white text-base mb-2">1. Protocol Sovereignty</h4>
                            <p className="text-xs">Users maintain exclusive control over private keys. GhostPay cannot initiate, reverse, or freeze transactions on the underlying blockchain networks.</p>
                        </div>
                        <div>
                            <h4 className="font-serif font-bold text-[#0A0B0F] dark:text-white text-base mb-2">2. Network Finality</h4>
                            <p className="text-xs">Payments are considered settled once confirmed on-chain. We do not support chargebacks; all disputes must be resolved via the integrated Escrow Mediation Protocol.</p>
                        </div>
                      </div>
                  </div>
              </Card>

              <Card noPadding className="overflow-hidden border-stone-200 dark:border-white/10 shadow-sm">
                  <div className="p-6 border-b border-stone-100 dark:border-white/5 bg-stone-50/50 dark:bg-white/[0.02] flex items-center gap-3">
                      <Shield className="text-nobel-gold shrink-0" size={20}/>
                      <h3 className="font-serif font-bold text-lg md:text-xl text-[#0A0B0F] dark:text-white">Privacy & Data Sovereignty</h3>
                  </div>
                  <div className="p-6 md:p-8 prose dark:prose-invert max-w-none text-xs md:text-sm text-stone-600 dark:text-stone-400 leading-relaxed space-y-4">
                      <p className="italic border-l-2 border-nobel-gold pl-4 bg-nobel-gold/5 py-2">
                          GhostPay is engineered for Absolute Silence.
                      </p>
                      <h4 className="font-bold text-[#0A0B0F] dark:text-white pt-2">Zero-Knowledge Standards</h4>
                      <ul className="list-disc pl-5 space-y-2 text-xs">
                          <li>Decoupled Identity: Metadata is stored in isolated nodes, never linked to public addresses on-chain.</li>
                          <li>Stealth Routing: Automatic generation of one-time settlement addresses to prevent transaction correlation.</li>
                      </ul>
                  </div>
              </Card>
          </div>

          <div className="space-y-8">
              <Card className="bg-[#0A0B0F] text-white border-white/10 overflow-hidden relative group">
                  <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 blur-xl transition-opacity">
                      <FileCheck size={160} />
                  </div>
                  <div className="relative z-10">
                    <h3 className="font-serif text-xl md:text-2xl mb-6">Audit Status TBA 2026</h3>
                    <div className="space-y-1 mb-8">
                        <AuditLineInternal title="SOLANA ANCHOR CONTRACT" status="PASSED • JAN '26" />
                        <AuditLineInternal title="SUI MOVE MODULE" status="PASSED • JAN '26" />
                        <AuditLineInternal title="GHOSTOS CORE RUNTIME" status="PASSED • DEC '25" />
                    </div>
                    <button className="w-full py-3.5 bg-white text-black rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-stone-200 transition-all shadow-xl flex items-center justify-center gap-2 group/btn">
                        VIEW SECURITY REGISTRY <ChevronRight size={14} className="group-hover/btn:translate-x-1 transition-transform"/>
                    </button>
                  </div>
              </Card>

              <Card>
                  <h3 className="font-serif font-bold text-xs sm:text-sm uppercase tracking-widest text-stone-500 mb-6 flex items-center gap-2">
                      <Gavel size={16} className="text-nobel-gold shrink-0"/> Regulatory Links
                  </h3>
                  <ul className="space-y-2 text-sm font-medium text-[#0A0B0F] dark:text-white">
                      <li>
                        <a href="#" className="p-3 rounded-xl hover:bg-stone-50 dark:hover:bg-white/5 transition-colors flex justify-between items-center group border border-stone-50 dark:border-white/5">
                            <span className="text-[10px] font-bold uppercase tracking-wide">GDPR-X Framework</span>
                            <ArrowUpRight size={14} className="text-stone-300 group-hover:text-nobel-gold" />
                        </a>
                      </li>
                      <li>
                        <a href="#" className="p-3 rounded-xl hover:bg-stone-50 dark:hover:bg-white/5 transition-colors flex justify-between items-center group border border-stone-50 dark:border-white/5">
                            <span className="text-[10px] font-bold uppercase tracking-wide">AML Exceptions</span>
                            <ArrowUpRight size={14} className="text-stone-300 group-hover:text-nobel-gold" />
                        </a>
                      </li>
                  </ul>
              </Card>

              <div className="p-6 bg-nobel-gold/5 rounded-3xl border border-dashed border-nobel-gold/20">
                  <div className="flex items-center gap-3 mb-3">
                      <Lock size={18} className="text-nobel-gold shrink-0" />
                      <span className="text-[9px] font-bold uppercase tracking-widest text-[#0A0B0F] dark:text-stone-300">Encryption Active</span>
                  </div>
                  <p className="text-[10px] text-stone-500 leading-relaxed">
                      All session data is encrypted and signed.
                  </p>
              </div>
          </div>
      </div>
    </div>
  );
};

const ArrowUpRight = ({ size, className }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M7 7h10v10"/><path d="M7 17 17 7"/>
    </svg>
);
